import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Button, Table, Space, Typography, Tag, Flex, Input, message, InputNumber, Tooltip, Select } from 'antd';
import { PrinterOutlined, EditOutlined, SaveOutlined, UndoOutlined } from '@ant-design/icons';
import { usePointOfSaleService } from '../../../services/pointofsale/PointOfSaleServiceProvider';
import { useReceiptPrinterService } from '../../../services/printing/ReceiptPrinterServiceProvider';
import { ReceiptPrinterService } from '../../../services/printing/ReceiptPrinterService';
import { Booking } from '../../../model/bookings/Booking';
import QuantitySelector from '../../common/QuantitySelector';
import { PointOfSaleTransactionItem } from '../../../model/pointofsale/PointOfSaleTransactionItem';
import { CreatePointOfSaleTransactionRequest } from '../../../model/pointofsale/CreatePointOfSaleTransactionRequest';
import { Slot } from '../../../model/bookings/Slot';
import { PointOfSaleBookingSlot } from '../../../model/pointofsale/PointOfSaleBookingSlot';
import { Facility } from '../../../model/facilities/Facility';
import { PointOfSaleTransaction } from '../../../model/pointofsale/PointOfSaleTransaction';
import { UpdatePaymentTransactionRequest } from '../../../model/pointofsale/UpdatePaymentTransactionRequest';
import { useFormatCurrency, useCurrencySymbol, useFormatDateTime } from '../../../hooks/useFormatters';
import { useBookingService } from '../../../services/bookings/BookingServiceProvider';

interface CheckInBookingProps {
  facility: Facility // TODO: Revise this as we should be able to get this from the facility itself
  booking: Booking;
  slot: Slot;
  isVisible: boolean;
  onClose: (wasSuccessful?: boolean) => void; 
}


interface BasketItem extends PointOfSaleTransactionItem {
  total: number;
}

const CheckInBooking: React.FC<CheckInBookingProps> = ({
  facility,
  booking,
  slot,
  isVisible,
  onClose
}) => {

  const pointOfSaleService = usePointOfSaleService();
  const formatCurrency = useFormatCurrency();
  const currencySymbol = useCurrencySymbol();
  const formatDateTime = useFormatDateTime();
  const bookingService = useBookingService();
  const [transaction, setTransaction] = useState<PointOfSaleTransaction | null>(null);
  const [isEditingFacilityCost, setIsEditingFacilityCost] = useState(false);
  const [editableFacilityCost, setEditableFacilityCost] = useState<number>(0);

  // A price fixed on the booking itself: an admin booking's stored price, or the price
  // stored on the slot's titled booking. null when the booking carries none.
  function storedBookingPrice(): number | null {
    if (booking.metadata?.Price !== undefined) {
      return parseFloat(booking.metadata.Price as string);
    }
    if (slot.metadata?.BookingTitle && slot.metadata?.Price !== undefined) {
      return parseFloat(slot.metadata.Price as string);
    }
    return null;
  }

  const receiptPrinterService = useReceiptPrinterService();
  const [items, setItems] = useState<PointOfSaleTransactionItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<PointOfSaleTransactionItem[]>([]);
  const [basket, setBasket] = useState<BasketItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortType, setSortType] = useState<'rank' | 'mostPopular'>('mostPopular');
  const [totalFacilityPrice, setTotalFacilityPrice] = useState(0);
  const [facilityPriceState, setFacilityPriceState] = useState<'pending' | 'ready' | 'failed'>('pending');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // The facility cost is the price the booking was saved with — check-in charges what
  // the member was quoted rather than working it out again. [FORT-288] In order:
  //   1. a price stored on the booking (admin / titled bookings);
  //   2. the booking slot prices on the booking's payment, recorded when it was booked;
  //   3. only for an older booking with neither, a quote from the API.
  // Staff can still edit the facility cost below before taking payment.
  useEffect(() => {
    let cancelled = false;
    const applyFacilityPrice = (price: number) => {
      setTotalFacilityPrice(price);
      setEditableFacilityCost(price);
      setFacilityPriceState('ready');
    };

    (async () => {
      const transactions = await pointOfSaleService.getTransactionsForBooking(booking.bookingId);
      if (cancelled) return;
      const latest = transactions?.sort((a, b) => b.transactionId - a.transactionId)[0];
      setTransaction(latest ?? null);

      const stored = storedBookingPrice();
      if (stored !== null && !isNaN(stored)) {
        applyFacilityPrice(stored);
        return;
      }
      if (latest && latest.bookingSlots?.length > 0) {
        applyFacilityPrice(latest.bookingSlots.reduce((sum, s) => sum + (s.price ?? 0), 0));
        return;
      }
      if (!isVisible) return;
      setFacilityPriceState('pending');
      const quote = await bookingService.getPriceQuote(
        booking.facilityName,
        booking.bookingDateTimeUtc,
        booking.bookingEndTimeUtc
      );
      if (cancelled) return;
      if (quote) {
        applyFacilityPrice(quote.price);
      } else {
        setFacilityPriceState('failed');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, booking.bookingId, pointOfSaleService, bookingService]);

  useEffect(() => {
    // Load sort preference from localStorage
    const savedSortType = localStorage.getItem('posItemSortType');
    if (savedSortType === 'rank' || savedSortType === 'mostPopular') {
      setSortType(savedSortType);
    }

    const loadPosItems = async () => {
      setIsLoading(true);
      try {
        const availableItems = await pointOfSaleService.getPosItems();
        const mappedItems = availableItems.map(item => ({ item: item, quantity: 0, overridePrice: 0 }));
        setItems(mappedItems);
        setFilteredItems(mappedItems); // Initialize filtered items with all items
      } catch (error) {
        console.error('Error loading POS items:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isVisible) {
      loadPosItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, pointOfSaleService]);

  const getItemRank = (transactionItem: PointOfSaleTransactionItem): number => {
    const rankStr = transactionItem.item.metadata?.Rank;
    if (rankStr) {
      const rank = parseInt(rankStr, 10);
      return isNaN(rank) ? Infinity : rank;
    }
    return Infinity;
  };

  const getItemTransactionCount = (transactionItem: PointOfSaleTransactionItem): number => {
    const countStr = transactionItem.item.metadata?.TransactionCount;
    if (countStr) {
      const count = parseInt(countStr, 10);
      return isNaN(count) ? 0 : count;
    }
    return 0;
  };

  const getItemTooltip = (transactionItem: PointOfSaleTransactionItem): string => {
    const parts: string[] = [];
    if (transactionItem.item.metadata?.Rank) {
      parts.push(`Rank: ${transactionItem.item.metadata.Rank}`);
    }
    if (transactionItem.item.metadata?.TransactionCount) {
      parts.push(`Transaction Count: ${transactionItem.item.metadata.TransactionCount}`);
    }
    return parts.length > 0 ? parts.join(', ') : '';
  };

  const sortItems = useCallback((itemsToSort: PointOfSaleTransactionItem[]): PointOfSaleTransactionItem[] => {
    const sorted = [...itemsToSort];
    if (sortType === 'rank') {
      sorted.sort((a, b) => {
        const rankA = getItemRank(a);
        const rankB = getItemRank(b);
        if (rankA === rankB) {
          return a.item.name.localeCompare(b.item.name);
        }
        return rankA - rankB;
      });
    } else {
      // mostPopular
      sorted.sort((a, b) => {
        const countA = getItemTransactionCount(a);
        const countB = getItemTransactionCount(b);
        if (countA === countB) {
          return a.item.name.localeCompare(b.item.name);
        }
        return countB - countA; // Descending order
      });
    }
    return sorted;
  }, [sortType]);

  const handleSortTypeChange = (value: 'rank' | 'mostPopular') => {
    setSortType(value);
    localStorage.setItem('posItemSortType', value);
  };

  // Filter and sort items based on search term and sort type
  useEffect(() => {
    let filtered: PointOfSaleTransactionItem[];
    if (!searchTerm.trim()) {
      filtered = items;
    } else {
      const lowerCaseSearch = searchTerm.toLowerCase();
      filtered = items.filter(item => 
        item.item.name.toLowerCase().includes(lowerCaseSearch)
      );
    }
    const sorted = sortItems(filtered);
    setFilteredItems(sorted);
  }, [searchTerm, items, sortItems]);

  const handleQuantityChange = (itemId: string, quantity: number) => {
    // First update the items state to show correct quantities in the table
    setItems(prevItems => prevItems.map(item => 
      item.item.id === itemId ? { ...item, quantity } : item
    ));
  
    // Now update the basket
    const item = items.find(i => i.item.id === itemId);
    if (!item) return;
  
    const updatedBasket = [...basket];
    const basketItemIndex = updatedBasket.findIndex(i => i.item.id === itemId);
    
  
    if (quantity > 0) {
      const basketItem: BasketItem = {
        ...item,
        quantity,
        total: item.item.price * quantity
      };
  
      if (basketItemIndex >= 0) {
        updatedBasket[basketItemIndex] = basketItem;
      } else {
        updatedBasket.push(basketItem);
      }
    } else if (basketItemIndex >= 0) {
      updatedBasket.splice(basketItemIndex, 1);
    }
  
    setBasket(updatedBasket);
  };

  // Derived, not stored: the facility cost now arrives asynchronously, and staff may
  // already have added POS items by then — the total must include both.
  const total = useMemo(
    () => totalFacilityPrice + basket.reduce((sum, item) => sum + item.total, 0),
    [totalFacilityPrice, basket]
  );
  // Nothing may be charged or recorded until the facility cost is known — a 0 written
  // here would overwrite the price saved on the booking's payment. [FORT-288]
  const facilityPriceReady = facilityPriceState === 'ready';

  const handleFacilityCostChange = (value: number | null) => {
    if (value !== null) {
      setEditableFacilityCost(value);
      setTotalFacilityPrice(value);
    }
  };

  const handleSaveFacilityCost = () => {
    setTotalFacilityPrice(editableFacilityCost);
    // A staff-set cost is a price to charge, even when the saved price couldn't be found.
    setFacilityPriceState('ready');
    setIsEditingFacilityCost(false);
    message.success('Facility cost updated successfully!');
  };

  const handleCancelFacilityCostEdit = () => {
    setEditableFacilityCost(totalFacilityPrice);
    setIsEditingFacilityCost(false);
  };

  const handleProcessPayment = async () => {
    if (!facilityPriceReady) return;
    setIsProcessingPayment(true);
    try {
      const facilityPricePaid: number = totalFacilityPrice; // Use the updated facility cost
      
      const pointOfSaleBookingSlot: PointOfSaleBookingSlot = {
        bookingSlot: slot,
        price: facilityPricePaid
      };

      if (transaction) {
        const request: UpdatePaymentTransactionRequest = {
          transactionId: transaction.transactionId,
          amount: total,
          status: "Completed",
          items: basket,
          bookingSlots: [pointOfSaleBookingSlot] 
        };

        await pointOfSaleService.updateTransaction(request);
      } else {

        const request: CreatePointOfSaleTransactionRequest = {
          membershipId: booking.membershipId,
          items: basket,
          bookingSlots: [pointOfSaleBookingSlot],
          amount: total,
          paymentTransactionStatus: "Completed"
        };
        await pointOfSaleService.makePayment(request);
      }
      
      // Print receipt after successful payment
      await handlePrintReceipt("Completed", true);
      
      onClose(true);
    } catch (error) {
      console.error('Error marking as paid:', error);
      onClose(false);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handlePrintReceipt = async (status: string, openTill: boolean = false) => {
    try {
      const receiptData = ReceiptPrinterService.createReceiptDataFromBooking(
        booking,
        slot,
        facility,
        basket,
        total,
        status
      );
           
      // Try to print the receipt via USB
      const printSuccess = await receiptPrinterService.printReceipt(receiptData, openTill);
      
      if (printSuccess) {
        if (status === "Completed") {
          message.success('Payment processed and receipt printed successfully!');
        } else {
          message.success('Receipt printed successfully!');
        }
      } else {
        // USB printing failed - offer to use browser printing as fallback
        if (status === "Completed") {
          message.warning('Payment processed successfully! USB printing failed. Would you like to print via browser?', 5);
        } else {
          message.warning('USB printing failed. Would you like to print via browser?', 5);
        }
        
        // Always fall back to browser printing if USB fails
        try {
          await receiptPrinterService.forcePrintViaBrowser(receiptData);
          message.info('Receipt printed via browser fallback.');
        } catch (browserPrintError) {
          console.error('Browser printing also failed:', browserPrintError);
          message.error('Both USB and browser printing failed.');
        }
      }
    } catch (printError) {
      console.error('Error printing receipt:', printError);
      // Payment was successful, so show success message but warn about receipt
      if (status === "Completed") {
        message.warning('Payment processed successfully! Receipt printing failed - check printer connection.');
      } else {
        message.error('Failed to print receipt.');
      }
    }
  };

  const handleMarkAsUnpaid = async () => {
    if (!facilityPriceReady) return;
    try {
      console.log('Marking as unpaid ', booking.bookingDateTimeUtc);
      const facilityPriceUnpaid: number = totalFacilityPrice; // Use the updated facility cost

      const pointOfSaleBookingSlot: PointOfSaleBookingSlot = {
        bookingSlot: slot,
        price: facilityPriceUnpaid
      };

      if (transaction) {
        const request: UpdatePaymentTransactionRequest = {
          transactionId: transaction.transactionId,
          amount: total,
          status: "Unpaid",
          items: basket,
          bookingSlots: [pointOfSaleBookingSlot] 
        };

        await pointOfSaleService.updateTransaction(request);
      } else {
        const request: CreatePointOfSaleTransactionRequest = {
          membershipId: booking.membershipId,
          items: basket,
          bookingSlots: [pointOfSaleBookingSlot],
          amount: total,
          paymentTransactionStatus: "Unpaid"
        };
        await pointOfSaleService.makePayment(request);
      }
      onClose(true);
    } catch (error) {
      console.error('Error marking as unpaid:', error);
      onClose(false);
    }
  };

  // Updated columns definition in CheckInBooking.tsx
  const columns = [
    {
      title: 'Item',
      dataIndex: ['item', 'name'],
      key: 'name',
      render: (text: string, record: PointOfSaleTransactionItem) => {
        const tooltip = getItemTooltip(record);
        if (tooltip) {
          return (
            <Tooltip title={tooltip}>
              <span style={{ cursor: 'help' }}>{text}</span>
            </Tooltip>
          );
        }
        return text;
      },
    },
    {
      title: 'Price',
      dataIndex: ['item', 'price'],
      key: 'price',
      render: (price: number) => formatCurrency(price ?? 0),
    },
    {
      title: 'Type',
      dataIndex: ['item', 'type'],
      key: 'type',
    },
    {
      title: 'Quantity',
      key: 'quantity',
      render: (value: number, record: PointOfSaleTransactionItem) => (
        <QuantitySelector
          value={record.quantity}
          onChange={(value) => handleQuantityChange(record.item.id, value)}
        />
      ),
    },
  ];

  return (
    <Modal
      data-testid="checkin-modal"
      title="Check In Booking"
      open={isVisible}
      onCancel={() => onClose(true)}
      width={800}
      footer={null}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Typography.Title level={5}>Booking Details</Typography.Title>
        <Space>
          <Tag color="blue">{booking.facilityName}</Tag>
          <Tag color="green">{formatDateTime(booking.bookingDateTimeUtc)}</Tag>
          <Tag color="purple">{booking.memberName}</Tag>
        </Space>

        <Flex justify="center" gap="middle">
          <Button
            data-testid="checkin-process-payment-btn"
            type="primary"
            onClick={handleProcessPayment}
            loading={isProcessingPayment}
            disabled={isProcessingPayment || !facilityPriceReady}
          >
            {isProcessingPayment ? 'Processing Payment...' : 'Process Payment'}
          </Button>
          <Button
            data-testid="checkin-mark-unpaid-btn"
            type="primary"
            danger
            onClick={handleMarkAsUnpaid}
            disabled={!facilityPriceReady}
          >
            Mark as Unpaid
          </Button>
          <Button 
            icon={<PrinterOutlined />} 
            onClick={() => handlePrintReceipt("Unpaid", false)}
            disabled={!facilityPriceReady}
          >
            Print Receipt
          </Button>
          <Button onClick={() => onClose(true)}>
            Cancel
          </Button>
        </Flex>

        <Space direction="vertical" style={{ width: '100%' }}>
          <Typography.Title level={5}>Summary</Typography.Title>
          
          {/* Editable Facility Cost Section */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            padding: '12px',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            backgroundColor: '#fafafa'
          }}>
            <Tooltip title="Click Edit to modify the facility cost for this booking">
              <Typography.Text strong>Facility Cost:</Typography.Text>
            </Tooltip>
            
            {isEditingFacilityCost ? (
              <Space>
                <InputNumber
                  inputMode="decimal"
                  value={editableFacilityCost}
                  onChange={handleFacilityCostChange}
                  precision={2}
                  min={0}
                  step={0.01}
                  prefix={currencySymbol}
                  style={{ width: '120px' }}
                />
                <Button 
                  type="primary" 
                  size="small" 
                  icon={<SaveOutlined />}
                  onClick={handleSaveFacilityCost}
                >
                  Save
                </Button>
                <Button 
                  size="small" 
                  icon={<UndoOutlined />}
                  onClick={handleCancelFacilityCostEdit}
                >
                  Cancel
                </Button>
              </Space>
            ) : (
              <Space>
                <Typography.Text data-testid="checkin-facility-cost">
                  {facilityPriceState === 'pending'
                    ? 'Loading…'
                    : facilityPriceState === 'failed'
                      ? 'Price unavailable — edit to set it'
                      : formatCurrency(totalFacilityPrice)}
                </Typography.Text>
                <Button 
                  type="text" 
                  size="small" 
                  icon={<EditOutlined />}
                  onClick={() => setIsEditingFacilityCost(true)}
                >
                  Edit
                </Button>
              </Space>
            )}
          </div>

          {basket.map(item => (
            <div key={item.item.id}>
              {item.item.name}: {formatCurrency(item.total)} ({item.quantity} x {formatCurrency(item.item.price)})
            </div>
          ))}
          <Typography.Title level={4}>Total: {formatCurrency(total)}</Typography.Title>
        </Space>        

        <Typography.Title level={5}>Additional Items</Typography.Title>
        <Space style={{ marginBottom: 16, width: '100%' }} direction="vertical" size="small">
          <Input.Search
            placeholder="Search items by name..."
            allowClear
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%' }}
          />
          <Select
            value={sortType}
            onChange={handleSortTypeChange}
            style={{ width: '150px' }}
          >
            <Select.Option value="mostPopular">Most Popular</Select.Option>
            <Select.Option value="rank">Rank</Select.Option>
          </Select>
        </Space>
        <Table
          dataSource={filteredItems}
          columns={columns}
          rowKey={(record) => record.item.id}
          pagination={false}
          loading={isLoading}
        />
      </Space>
    </Modal>
  );
};

export default CheckInBooking;